import { Component, OnInit } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { PageEvent } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import GridsConfig from "@app/common/grid.config";
import { Migration } from "@app/interface/interfaces";
import { PaginatorFilter } from "@app/models/grids";
import { ApiService } from "@app/services/api.service";
import {
  ExcelExportService,
  ExcelColumnDef,
} from "@app/services/excel-export.service";
import { MigrationService } from "@app/services/migration.service";
import { MigrationStore } from "@app/services/migration.store";
import { ColumnSortConfig } from "@app/components/table-grid/table-grid/table-grid.component";
import { catchError, map, of, tap } from "rxjs";
import { UtilsService } from "@app/services/utils.service";

@Component({
  selector: "app-migrations",
  templateUrl: "./migrations.component.html",
  styleUrl: "./migrations.component.scss",
  standalone: false,
})
export class MigrationsComponent implements OnInit {
  constructor(
    private migrationService: MigrationService,
    private apiService: ApiService,
    private excelExportService: ExcelExportService,
    private migrationStore: MigrationStore,
    private router: Router,
    private snackBar: MatSnackBar,
    private utilsService: UtilsService,
  ) {}

  // Migrations table
  migrations: Migration[] = [];
  paginatedMigrations: Migration[] = [];
  displayedColumns = GridsConfig.Migrations.Columns;
  columnHeaders = GridsConfig.Migrations.Headers;

  /** Column type configuration for type-aware sorting */
  readonly columnSortTypes: ColumnSortConfig = {
    id: "number",
    company_id: "number",
    company_name: "string",
    created_by: "string",
    created_dt_local: "date",
    progressPercentageLabel: "number",
    status: "string",
  };

  /** Current sort state */
  currentSort: Sort = { active: "id", direction: "desc" };

  get currentSortColumn(): string {
    return this.currentSort.active || "id";
  }

  get currentSortDirection(): "asc" | "desc" {
    return this.currentSort.direction === "asc" ? "asc" : "desc";
  }

  /** Excel column definitions â€” exact header names per AC2 */
  readonly excelColumns: ExcelColumnDef[] = [
    { key: "id", header: "Id", type: "number", width: 10 },
    { key: "company_id", header: "Company Id", type: "number", width: 14 },
    { key: "company_name", header: "Company Name", type: "string", width: 30 },
    { key: "created_by", header: "Created By", type: "string", width: 28 },
    { key: "created_dt", header: "Created On", type: "date", width: 24 },
    {
      key: "progressPercentage",
      header: "Progress",
      type: "number",
      width: 12,
    },
    { key: "status", header: "Status", type: "string", width: 18 },
  ];

  // paginator
  paginatorFilter: PaginatorFilter = new PaginatorFilter({ pageSize: 10 });
  //loading
  loading: boolean = false;
  isAutoLoadChecked: boolean = true;
  isFirstLoad: boolean = true;
  exporting: boolean = false;

  // Deleted migrations
  deletedMigrations: Migration[] = [];
  deletedMigrationsPaginated: Migration[] = [];
  deletedMigrationsColumns = GridsConfig.DeletedMigrations.Columns;
  deletedMigrationsHeaders = GridsConfig.DeletedMigrations.Headers;
  deletedPaginatorFilter: PaginatorFilter = new PaginatorFilter({
    pageSize: 5,
  });
  deletedLoading: boolean = false;
  deletedExpanded: boolean = false;

  ngOnInit() {
    this.getMigrations();
  }

  /** Handle sort changes from table-grid */
  onSortChanged(sort: Sort): void {
    this.currentSort = sort;
    this.paginatorFilter.currentPage = 0; // Reset to first page on sort change
    this.applyMigrationsPagination();
  }

  /** Handle cell click events from table-grid */
  onCellClicked(event: { element: Migration; column: string }): void {
    if (event.column === "id") {
      this.showMigrationMoreDetails(event.element);
    }
  }

  executeMigrations($event: any | null) {
    this.getMigrations();
  }

  getMigrations() {
    this.changeLoadingState(true);
    this.apiService
      .getMigrations()
      .pipe(
        map((migrations: Migration[]) =>
          migrations.map((migration) => {
            return this.migrationService.processMigrationRecord(migration);
          }),
        ),
        tap((processedMigrations: Migration[]) => {
          this.migrations = processedMigrations;
          this.paginatorFilter.count = this.migrations.length;
          this.applyMigrationsPagination();
          this.changeLoadingState(false);
        }),
        catchError((error) => {
          this.changeLoadingState(false);
          return of([]);
        }),
      )
      .subscribe();
  }

  paginateMigrations(event: PageEvent) {
    this.paginatorFilter.currentPage = event.pageIndex;
    this.paginatorFilter.pageSize = event.pageSize;
    this.applyMigrationsPagination();
  }

  showMigrationMoreDetails(migration: Migration) {
    // If status is exception, navigate to fallout report with migration ID
    if (migration.status === "exception") {
      this.router.navigate(["/metrics-tracker"], {
        queryParams: { migrationId: migration.id },
      });
    } else {
      // Otherwise, navigate to migration details page
      this.router.navigate([`/migration/${migration.id}`]);
    }
  }

  /** Export to .xlsx with typed cells, exact headers, and filename */
  async exportToExcel(): Promise<void> {
    if (this.migrations.length === 0) {
      return;
    }

    this.exporting = true;
    try {
      // Export the full sorted dataset (matches what user sees)
      const sortedData = this.getSortedMigrations();
      await this.excelExportService.exportToExcel(
        sortedData as unknown as Record<string, unknown>[],
        this.excelColumns,
      );
      this.exporting = false;
    } catch (error) {
      this.exporting = false;
      const errorRefId = `ERR-${Date.now()}`;
      console.error(`Export failed [${errorRefId}]:`, error);

      // Non-technical message with retry option and error reference ID
      const snackBarRef = this.snackBar.open(
        `Export failed. Please try again. (Ref: ${errorRefId})`,
        "Retry",
        {
          duration: 10000,
          panelClass: ["snackbar-error"],
          horizontalPosition: "center",
          verticalPosition: "bottom",
        },
      );
      snackBarRef.onAction().subscribe(() => {
        this.exportToExcel();
      });
    }
  }

  onDeletedPanelOpened() {
    if (this.deletedMigrations.length === 0) {
      this.getDeletedMigrations();
    }
  }

  getDeletedMigrations() {
    this.deletedLoading = true;
    this.apiService
      .getDeletedMigrations()
      .pipe(
        map((migrations: Migration[]) =>
          migrations.map((migration) => {
            return this.migrationService.processMigrationRecord(migration);
          }),
        ),
        tap((processedMigrations: Migration[]) => {
          this.deletedMigrations = this.utilsService.sortArrayByNumber(
            processedMigrations,
            "id",
          );
          this.applyDeletedMigrationsPagination();
          this.deletedLoading = false;
        }),
        catchError((error) => {
          this.deletedLoading = false;
          return of([]);
        }),
      )
      .subscribe();
  }

  paginateDeletedMigrations(event: PageEvent) {
    this.deletedPaginatorFilter.currentPage = event.pageIndex;
    this.deletedPaginatorFilter.pageSize = event.pageSize;
    this.applyDeletedMigrationsPagination();
  }

  // ******** PRIVATE ********
  private applyDeletedMigrationsPagination() {
    const skipRecords: number =
      this.deletedPaginatorFilter.currentPage *
      this.deletedPaginatorFilter.pageSize;
    this.deletedMigrationsPaginated = this.deletedMigrations
      .slice(skipRecords)
      .slice(0, this.deletedPaginatorFilter.pageSize);
    this.deletedPaginatorFilter.count = this.deletedMigrations.length;
  }

  private applyMigrationsPagination() {
    // Sort the full dataset, then paginate
    const sorted = this.getSortedMigrations();
    const startIndex =
      this.paginatorFilter.currentPage * this.paginatorFilter.pageSize;
    const endIndex = startIndex + this.paginatorFilter.pageSize;
    this.paginatedMigrations = sorted.slice(startIndex, endIndex);
    this.paginatorFilter.count = this.migrations.length;
  }

  /** Return migrations sorted by the current sort state */
  private getSortedMigrations(): Migration[] {
    if (!this.currentSort.active || this.currentSort.direction === "") {
      // No active sort â€” default to Created On descending
      return [...this.migrations].sort(
        (a, b) =>
          new Date(b.created_dt).getTime() - new Date(a.created_dt).getTime(),
      );
    }
    const direction = this.currentSort.direction === "asc" ? 1 : -1;
    const active = this.currentSort.active;
    const sortType = this.columnSortTypes[active] || "string";

    return [...this.migrations].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortType) {
        case "number":
          // For progressPercentageLabel, use the underlying progressPercentage value
          if (active === "progressPercentageLabel") {
            aVal = a.progressPercentage ?? 0;
            bVal = b.progressPercentage ?? 0;
          } else {
            aVal = (a as any)[active] ?? 0;
            bVal = (b as any)[active] ?? 0;
          }
          break;
        case "date":
          // Handle date fields - look for raw date field if display field is used
          const rawDateField = active.replace("_local", "");
          aVal = (a as any)[rawDateField]
            ? new Date((a as any)[rawDateField]).getTime()
            : 0;
          bVal = (b as any)[rawDateField]
            ? new Date((b as any)[rawDateField]).getTime()
            : 0;
          break;
        case "string":
        default:
          aVal = ((a as any)[active] || "").toString().toLowerCase();
          bVal = ((b as any)[active] || "").toString().toLowerCase();
          break;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }

  private changeLoadingState(state: boolean) {
    this.loading = state;
    this.migrationStore.setLoading(state);
    if (this.isFirstLoad) {
      this.isFirstLoad =
        this.isFirstLoad && !this.loading ? false : this.isFirstLoad;
    }
  }
}
